import { useFrame } from '@react-three/fiber';
import { Mesh, Vector3, Euler, Clock } from 'three';
import { Spaceship } from './Spaceship';
import { useRef, useState, useEffect } from 'react';

interface OtherPlayerSpaceshipProps {
  position: [number, number, number];
  rotation: [number, number, number];
  playerId: string; // ID do jogador para tracking
  velocity?: { x: number, y: number, z: number }; // Velocidade recebida do servidor
  timestamp?: number; // Timestamp da última atualização
}

// Configurações de interpolação simplificadas para movimentos mais estáveis
const LERP_FACTOR = 0.1; // Fator de interpolação mais direto
const THRESHOLD = 0.01;  // Limiar mais alto para evitar tremulações
const PREDICTION_SCALE = 1.0; // Sem predição para evitar sobrecorreção
const EASING_POWER = 2.0; // Curva de easing mais simples

// Componente para as naves de outros jogadores
// Usa o mesmo componente Spaceship para manter a aparência idêntica
const OtherPlayerSpaceship: React.FC<OtherPlayerSpaceshipProps> = (props) => {
  const {
    position,
    rotation,
    playerId, // Usando o playerId para debug e identificação
  } = props;

  const shipRef = useRef<Mesh>(null);
  const clock = useRef<Clock>(new Clock());

  // Estado para controle de movimento
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(Date.now());
  const [targetPosition, setTargetPosition] = useState<Vector3>(new Vector3(position[0], position[1], position[2]));
  const [targetRotation, setTargetRotation] = useState<Euler>(new Euler(rotation[0], rotation[1], rotation[2]));
  const [velocity, setVelocity] = useState<Vector3>(new Vector3(0, 0, 0));
  // Velocidade suavizada para movimentos mais fluidos
  const [smoothedVelocity, setSmoothedVelocity] = useState<Vector3>(new Vector3(0, 0, 0));
  const [previousPosition, setPreviousPosition] = useState<Vector3>(new Vector3(position[0], position[1], position[2]));
  const [lastPositionUpdate, setLastPositionUpdate] = useState<number>(Date.now());

  // Quando position ou rotation mudam, atualiza os alvos e o timestamp
  useEffect(() => {
    const now = Date.now();
    const newTarget = new Vector3(position[0], position[1], position[2]);

    // Log para debug (apenas para mudanças significativas)
    const distanceFromCurrent = newTarget.distanceTo(previousPosition);
    if (distanceFromCurrent > THRESHOLD * 10) {
      console.log(`Player ${playerId} received significant position update:`, position);
    }

    // Tempo desde a última atualização em segundos
    const timeDeltaSeconds = (now - lastUpdateTime) / 1000;

    // Usar a velocidade fornecida pelo servidor se disponível, caso contrário calcular
    let newVelocity;
    if (props.velocity) {
      // Usar a velocidade do servidor
      newVelocity = new Vector3(props.velocity.x, props.velocity.y, props.velocity.z);
    } else if (timeDeltaSeconds > 0) {
      // Calcular velocidade atual com base na mudança de posição
      newVelocity = new Vector3();
      newVelocity.subVectors(newTarget, previousPosition).divideScalar(Math.max(timeDeltaSeconds, 0.016));
    } else {
      newVelocity = velocity.clone(); // Manter a velocidade atual
    }

    // Suaviza a velocidade para evitar mudanças bruscas, com mais peso para a nova velocidade
    // Usando uma nova velocidade temporária para cálculos
    const tempSmoothedVelocity = new Vector3();
    tempSmoothedVelocity.addVectors(
      velocity.clone().multiplyScalar(0.1), // 10% da velocidade anterior (reduzido ainda mais)
      newVelocity.clone().multiplyScalar(0.9)  // 90% da nova velocidade (aumentado ainda mais)
    );

    // Atualiza a velocidade principal
    setVelocity(tempSmoothedVelocity.clone());

    // Atualiza a velocidade suavizada com uma interpolação mais gradual
    // Isso cria uma transição ainda mais suave entre estados de velocidade
    const newSmoothedVelocity = new Vector3();
    newSmoothedVelocity.addVectors(
      smoothedVelocity.clone().multiplyScalar(0.4), // 40% da velocidade suavizada anterior
      tempSmoothedVelocity.clone().multiplyScalar(0.6)  // 60% da nova velocidade calculada
    );
    setSmoothedVelocity(newSmoothedVelocity);

    // Se a velocidade for muito baixa mas não zero, aplicamos uma velocidade mínima
    if (tempSmoothedVelocity.length() > 0 && tempSmoothedVelocity.length() < 0.1) {
      const minVelocity = tempSmoothedVelocity.clone().normalize().multiplyScalar(0.2);
      setVelocity(minVelocity);
      setSmoothedVelocity(minVelocity.clone());
    }

    // Armazena a posição anterior e o timestamp para cálculos futuros
    setPreviousPosition(targetPosition.clone());
    setTargetPosition(newTarget);
    setLastUpdateTime(props.timestamp || now);

    // Resetar o clock para termos um delta time preciso
    clock.current.getDelta();

    // Atualiza a rotação alvo
    const newRotation = new Euler(rotation[0], rotation[1], rotation[2]);
    setTargetRotation(newRotation);
  }, [position, rotation, props.velocity, props.timestamp, playerId]);

  // Atualizar posição e rotação suavemente em cada frame - lógica simplificada
  useFrame(() => {
    if (shipRef.current) {
      const ship = shipRef.current;
      const deltaTime = clock.current.getDelta();
      const now = Date.now();

      // Tempo desde a última atualização de posição do servidor
      const serverUpdateAge = (now - lastUpdateTime) / 1000; // em segundos

      // Atualizar velocidade suavizada com interpolação simples
      smoothedVelocity.lerp(velocity, 0.1);

      // Posição alvo sem predição complexa
      const targetPos = targetPosition.clone();

      // Calcular distância até o alvo
      const dist = ship.position.distanceTo(targetPos);

      // Forçar atualização se a posição não mudou por muito tempo (2 segundos)
      if (now - lastPositionUpdate > 2000) {
        ship.position.copy(targetPos);
        setLastPositionUpdate(now);
        if (process.env.NODE_ENV === 'development') {
          console.log(`Forced position update for player ${playerId}`);
        }
      }
      // Interpolação simples para posição
      else {
        // Se estamos muito próximos do alvo, apenas definimos a posição final
        if (dist < THRESHOLD) {
          ship.position.copy(targetPos);
        } else {
          // Interpolação linear simples
          const lerpFactor = Math.min(LERP_FACTOR * deltaTime * 60, 0.2);
          ship.position.lerp(targetPos, lerpFactor);

          // Atualizar timestamp da última mudança de posição
          if (dist > THRESHOLD) {
            setLastPositionUpdate(now);
          }
        }
      }

      // Interpolação simples para rotação
      const rotationFactor = Math.min(LERP_FACTOR * deltaTime * 60, 0.2);

      ship.rotation.x += (targetRotation.x - ship.rotation.x) * rotationFactor;
      ship.rotation.y += (targetRotation.y - ship.rotation.y) * rotationFactor;
      ship.rotation.z += (targetRotation.z - ship.rotation.z) * rotationFactor;

      // Rotacionar a nave na direção do movimento se houver velocidade significativa
      if (smoothedVelocity.length() > 0.1) {
        const direction = smoothedVelocity.clone().normalize();
        const targetYRotation = Math.atan2(direction.x, -direction.z);
        ship.rotation.y += (targetYRotation - ship.rotation.y) * rotationFactor;
      }
    }
  });

  // Adicionamos o playerId como atributo de dados para debug ou referência futura
  return (
    <Spaceship
      ref={shipRef}
      position={position}
      rotation={rotation}
      isRemotePlayer={true} // Indica que esta é uma nave de outro jogador
      data-player-id={playerId} // Usando o playerId como atributo de dados para debugging e queries
    />
  );
};

export default OtherPlayerSpaceship;
